import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { adsApiCall } from "./api";
import CreditsLedger from "./CreditsLedger";
import Achievements from "./Achievements";
import { Loader2, Zap, Link2, Check, X, Share2, Twitter, Facebook, Linkedin, MessageCircle, Send, Mail, BarChart3, Eye, MousePointerClick, Bell, BellOff, Gift, Users, ToggleLeft, ToggleRight, Sparkles, Wallet, FileText, Award } from "lucide-react";
import { toast } from "sonner";

export default function CampaignsPanel() {
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [shareFor, setShareFor] = useState(null); // campaign object being shared
  const [leadsFor, setLeadsFor] = useState(null); // campaign showing captured leads
  const [ledgerOpen, setLedgerOpen] = useState(false);
  const [achievementsOpen, setAchievementsOpen] = useState(false);
  const [digestOn, setDigestOn] = useState(null); // null=unknown, true/false after fetch
  const [statementOn, setStatementOn] = useState(null);

  async function refresh() {
    setLoading(true);
    try {
      const r = await adsApiCall.listCampaigns();
      setCampaigns(r.campaigns || []);
      const d = await adsApiCall.digestStatus().catch(() => null);
      if (d) setDigestOn(d.subscribed);
      const s = await adsApiCall.statementStatus().catch(() => null);
      if (s) setStatementOn(s.subscribed);
    } catch (e) {
      if (e?.response?.status !== 401) toast.error("Failed to load campaigns");
    } finally { setLoading(false); }
  }
  useEffect(() => { refresh(); }, []);

  async function toggleDigest() {
    try {
      const r = digestOn ? await adsApiCall.digestUnsubscribe() : await adsApiCall.digestSubscribe();
      setDigestOn(r.subscribed);
      toast.success(r.subscribed ? "Weekly digest ON" : "Weekly digest OFF");
    } catch {
      toast.error("Couldn't update digest preference");
    }
  }

  async function toggleStatement() {
    try {
      const r = statementOn ? await adsApiCall.statementUnsubscribe() : await adsApiCall.statementSubscribe();
      setStatementOn(r.subscribed);
      toast.success(r.subscribed ? "Monthly statement ON" : "Monthly statement OFF");
    } catch {
      toast.error("Couldn't update statement preference");
    }
  }

  async function toggleShare(c) {
    try {
      if (c.is_public) {
        await adsApiCall.unshareCampaign(c.campaign_id);
        toast.success("Campaign is now private");
      } else {
        await adsApiCall.shareCampaign(c.campaign_id);
        toast.success("Campaign is now public — share away!");
        setShareFor({ ...c, is_public: true });
      }
      refresh();
    } catch {
      toast.error("Couldn't update sharing");
    }
  }

  if (loading) return (
    <div className="ads-empty-state" style={{ padding: "24px 0" }} data-testid="campaigns-loading">
      <Loader2 className="animate-spin" size={18} />
    </div>
  );

  if (!campaigns.length) return null;

  return (
    <div style={{ marginBottom: 32 }} data-testid="campaigns-panel">
      <div className="cp-header-row">
        <div className="ads-panel-title" style={{ fontSize: 13, marginBottom: 0 }}>
          <Zap size={12} style={{ display: "inline", marginRight: 6 }} /> My Instant Campaigns
        </div>
        {digestOn !== null && (
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
            <button
              className="ads-btn ads-btn-ghost"
              style={{ padding: "6px 12px", fontSize: 12 }}
              onClick={() => setLedgerOpen(true)}
              data-testid="cp-ledger-open"
              title="See every credit earned and spent"
            >
              <Wallet size={12} /> Credits ledger
            </button>
            <button
              className="ads-btn ads-btn-ghost"
              style={{ padding: "6px 12px", fontSize: 12 }}
              onClick={() => setAchievementsOpen(true)}
              data-testid="cp-achievements-open"
              title="Your unlocked achievement badges"
            >
              <Award size={12} /> Achievements
            </button>
            <button
              className="ads-btn ads-btn-ghost"
              style={{ padding: "6px 12px", fontSize: 12 }}
              onClick={toggleDigest}
              data-testid="cp-digest-toggle"
              title={digestOn
                ? "You'll get a weekly performance recap by email"
                : "Turn on a weekly email with your campaign stats"}
            >
              {digestOn ? <><Bell size={12} /> Weekly digest: ON</> : <><BellOff size={12} /> Weekly digest: OFF</>}
            </button>
            {statementOn !== null && (
              <button
                className="ads-btn ads-btn-ghost"
                style={{ padding: "6px 12px", fontSize: 12 }}
                onClick={toggleStatement}
                data-testid="cp-statement-toggle"
                title={statementOn
                  ? "You'll receive a monthly credit statement with CSV ledger"
                  : "Turn on a monthly email with your credits recap + CSV"}
              >
                <FileText size={12} /> Monthly statement: <b style={{ marginLeft: 4 }}>{statementOn ? "ON" : "OFF"}</b>
              </button>
            )}
          </div>
        )}
      </div>
      <div className="cp-grid">
        {campaigns.map((c) => (
          <div key={c.campaign_id} className="cp-card" data-testid={`cp-${c.campaign_id}`}>
            <div className="cp-head">
              <div className="cp-title">{c.title}</div>
              <span className={`cp-status ${c.is_public ? "public" : "private"}`}>
                {c.is_public ? "Public" : "Private"}
              </span>
            </div>
            <div className="cp-sub">{c.count} ads · {c.angles.length} angles</div>
            {c.referral_signups > 0 && (
              <div className="cp-referral" data-testid={`cp-referral-${c.campaign_id}`} title="Credits earned from people who signed up via this campaign link">
                <Gift size={12} />
                <span>
                  <b>{c.referral_signups}</b> signup{c.referral_signups === 1 ? "" : "s"} · <b>+{c.referral_credits}</b> credits earned
                </span>
              </div>
            )}
            {c.remix_count > 0 && (
              <div className="cp-remix" data-testid={`cp-remix-${c.campaign_id}`} title="Credits earned from remixes of this campaign">
                <Sparkles size={12} />
                <span>
                  <b>{c.remix_count}</b> remix{c.remix_count === 1 ? "" : "es"} · <b>+{c.remix_credits}</b> credits earned
                </span>
              </div>
            )}
            {c.is_public && <CampaignStatsBar campaignId={c.campaign_id} />}
            <div className="cp-actions">
              <button
                className="ads-btn ads-btn-ghost"
                style={{ padding: "6px 10px", fontSize: 12 }}
                onClick={() => toggleShare(c)}
                data-testid={`cp-share-${c.campaign_id}`}
              >
                {c.is_public ? (<><X size={12} /> Make private</>) : (<><Share2 size={12} /> Share</>)}
              </button>
              {c.is_public && (
                <button
                  className="ads-btn ads-btn-ghost"
                  style={{ padding: "6px 10px", fontSize: 12 }}
                  onClick={() => setShareFor(c)}
                  data-testid={`cp-link-${c.campaign_id}`}
                >
                  <Link2 size={12} /> Link
                </button>
              )}
              {c.leads_count > 0 && (
                <button
                  className="ads-btn ads-btn-ghost"
                  style={{ padding: "6px 10px", fontSize: 12 }}
                  onClick={() => setLeadsFor(c)}
                  data-testid={`cp-leads-${c.campaign_id}`}
                  title="View captured leads"
                >
                  <Users size={12} /> {c.leads_count} lead{c.leads_count === 1 ? "" : "s"}
                </button>
              )}
            </div>
          </div>
        ))}
      </div>

      {shareFor && <ShareModal campaign={shareFor} onClose={() => { setShareFor(null); refresh(); }} />}
      {leadsFor && <LeadsModal campaign={leadsFor} onClose={() => setLeadsFor(null)} />}
      {ledgerOpen && <CreditsLedger onClose={() => setLedgerOpen(false)} />}
      {achievementsOpen && <Achievements onClose={() => setAchievementsOpen(false)} />}
    </div>
  );
}

function LeadsModal({ campaign, onClose }) {
  const [leads, setLeads] = useState(null);
  useEffect(() => {
    adsApiCall.listLeads(campaign.campaign_id)
      .then((r) => setLeads(r))
      .catch(() => toast.error("Couldn't load leads"));
  }, [campaign.campaign_id]);

  return (
    <div className="cp-modal-backdrop" onClick={onClose} data-testid="cp-leads-modal">
      <div className="cp-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 560 }}>
        <button className="ads-mobile-sheet-close" style={{ display: "flex" }} onClick={onClose} aria-label="Close">
          <X size={16} />
        </button>
        <div className="cp-modal-title">Leads captured</div>
        <div className="cp-modal-sub">
          {leads ? `${leads.count} total · ${leads.claimed} claimed their bonus` : "Loading…"}
        </div>
        {!leads ? (
          <div style={{ textAlign: "center", padding: "24px 0" }}>
            <Loader2 className="animate-spin" size={20} />
          </div>
        ) : leads.count === 0 ? (
          <div className="ads-empty-state">No leads yet — share the campaign to start collecting.</div>
        ) : (
          <div className="cp-leads-list" data-testid="cp-leads-list">
            {leads.leads.map((l, i) => (
              <div key={i} className={`cp-lead-row ${l.claimed ? "claimed" : ""}`} data-testid={`cp-lead-${i}`}>
                <Mail size={13} style={{ color: "var(--ads-text-dim)" }} />
                <span className="cp-lead-email">{l.email}</span>
                {l.claimed && <span className="cp-lead-badge">Claimed</span>}
                <span className="cp-lead-date">{new Date(l.created_at).toLocaleDateString()}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function CampaignStatsBar({ campaignId }) {
  const [stats, setStats] = useState(null);
  useEffect(() => {
    adsApiCall.campaignStats(campaignId).then(setStats).catch(() => {});
  }, [campaignId]);
  if (!stats) return null;
  const { views, unique_visitors, cta_clicks, top_channel } = stats;
  if (!views && !cta_clicks) {
    return (
      <div className="cp-stats-empty" data-testid={`cp-stats-${campaignId}`}>
        <BarChart3 size={11} /> No views yet — share to get the first one.
      </div>
    );
  }
  const ctr = views > 0 ? Math.round((cta_clicks / views) * 100) : 0;
  return (
    <div className="cp-stats" data-testid={`cp-stats-${campaignId}`}>
      <div className="cp-stat" title="Unique visitors">
        <Eye size={12} />
        <span>
          <b>{unique_visitors || views}</b> views
        </span>
      </div>
      <div className="cp-stat" title="CTA clicks">
        <MousePointerClick size={12} />
        <span>
          <b>{cta_clicks}</b> clicks
          {ctr > 0 && <span className="cp-stat-hint"> · {ctr}% CTR</span>}
        </span>
      </div>
      {top_channel && (
        <div className="cp-stat" title="Top share channel">
          <Share2 size={12} />
          <span>
            top: <b>{top_channel}</b>
          </span>
        </div>
      )}
    </div>
  );
}

function ShareModal({ campaign, onClose }) {
  const [copied, setCopied] = useState(false);
  const [slug, setSlug] = useState(campaign.slug || "");
  const [slugState, setSlugState] = useState({ checking: false, available: null, saving: false });
  const [leadsOn, setLeadsOn] = useState(!!campaign.leads_enabled);
  const [leadsSaving, setLeadsSaving] = useState(false);
  const publicKey = campaign.slug || campaign.campaign_id;
  const url = `${window.location.origin}/c/${slug || publicKey}`;
  const msg = `I just generated 25 ads in one click — "${campaign.title}". Full campaign 👉`;
  const fullMsg = `${msg} ${url}`;
  const encMsg = encodeURIComponent(fullMsg);
  const encUrl = encodeURIComponent(url);
  const encTitle = encodeURIComponent(`${campaign.title} — 25 ads in one prompt`);

  // Debounced slug availability check
  useEffect(() => {
    const s = slug.trim().toLowerCase();
    if (!s || s === (campaign.slug || "")) {
      setSlugState({ checking: false, available: null, saving: false });
      return;
    }
    setSlugState((st) => ({ ...st, checking: true }));
    const t = setTimeout(async () => {
      try {
        const r = await adsApiCall.slugAvailable(s);
        setSlugState({ checking: false, available: r.available, saving: false, reason: r.reason });
      } catch {
        setSlugState({ checking: false, available: null, saving: false });
      }
    }, 350);
    return () => clearTimeout(t);
  }, [slug, campaign.slug]);

  async function saveSlug() {
    const s = slug.trim().toLowerCase();
    if (!s) return;
    setSlugState((st) => ({ ...st, saving: true }));
    try {
      const r = await adsApiCall.setCampaignSlug(campaign.campaign_id, s);
      toast.success(`Custom URL set: /c/${r.slug}`);
      campaign.slug = r.slug;
      setSlugState({ checking: false, available: true, saving: false });
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(detail?.message || detail || "Couldn't save URL");
      setSlugState((st) => ({ ...st, saving: false }));
    }
  }

  async function clearSlug() {
    try {
      await adsApiCall.deleteCampaignSlug(campaign.campaign_id);
      campaign.slug = null;
      setSlug("");
      toast.success("Custom URL removed");
    } catch {
      toast.error("Couldn't remove URL");
    }
  }

  async function toggleLeads() {
    setLeadsSaving(true);
    const next = !leadsOn;
    try {
      await adsApiCall.setLeadsEnabled(campaign.campaign_id, next);
      setLeadsOn(next);
      campaign.leads_enabled = next;
      toast.success(next ? "Lead capture: ON" : "Lead capture: OFF");
    } catch {
      toast.error("Couldn't update lead capture");
    } finally {
      setLeadsSaving(false);
    }
  }

  async function copy() {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      toast.error("Couldn't copy");
    }
  }

  async function nativeShare() {
    try {
      if (navigator.share) {
        await navigator.share({ title: campaign.title, text: msg, url });
      } else {
        await copy();
      }
    } catch {}
  }

  const channels = [
    { id: "whatsapp", label: "WhatsApp", icon: MessageCircle, color: "#25D366",
      href: `https://wa.me/?text=${encMsg}` },
    { id: "telegram", label: "Telegram", icon: Send, color: "#26A5E4",
      href: `https://t.me/share/url?url=${encUrl}&text=${encodeURIComponent(msg)}` },
    { id: "twitter",  label: "X / Twitter", icon: Twitter, color: "#000000",
      href: `https://twitter.com/intent/tweet?text=${encMsg}` },
    { id: "facebook", label: "Facebook", icon: Facebook, color: "#1877F2",
      href: `https://www.facebook.com/sharer/sharer.php?u=${encUrl}` },
    { id: "linkedin", label: "LinkedIn", icon: Linkedin, color: "#0A66C2",
      href: `https://www.linkedin.com/sharing/share-offsite/?url=${encUrl}` },
    { id: "reddit",   label: "Reddit",   icon: Share2, color: "#FF4500",
      href: `https://www.reddit.com/submit?url=${encUrl}&title=${encTitle}` },
    { id: "pinterest", label: "Pinterest", icon: Share2, color: "#E60023",
      href: `https://pinterest.com/pin/create/button/?url=${encUrl}&description=${encTitle}` },
    { id: "email",    label: "Email",    icon: Mail, color: "#6b7280",
      href: `mailto:?subject=${encTitle}&body=${encMsg}` },
  ];

  const slugHint = slugState.checking ? "Checking…"
    : slug.trim() && slugState.available === false ? (slugState.reason === "invalid" ? "Invalid — use a-z, 0-9 and dashes" : "That URL is taken")
    : slug.trim() && slugState.available === true ? "✓ Available"
    : "";
  const canSaveSlug = slug.trim().toLowerCase() !== (campaign.slug || "") && slugState.available === true && !slugState.saving;

  return (
    <div className="cp-modal-backdrop" onClick={onClose} data-testid="cp-share-modal">
      <div className="cp-modal" onClick={(e) => e.stopPropagation()}>
        <button className="ads-mobile-sheet-close" style={{ display: "flex" }} onClick={onClose} aria-label="Close">
          <X size={16} />
        </button>
        <div className="cp-modal-title">Share your campaign</div>
        <div className="cp-modal-sub">Anyone with this link can preview your 25 ads (read-only).</div>

        {/* Vanity slug editor */}
        <div className="cp-slug-editor" data-testid="cp-slug-editor">
          <div className="cp-slug-label">Custom URL (optional)</div>
          <div className="cp-slug-row">
            <span className="cp-slug-prefix">{window.location.origin}/c/</span>
            <input
              className="cp-slug-input"
              placeholder="eco-shoes-launch"
              value={slug}
              onChange={(e) => setSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ""))}
              maxLength={40}
              data-testid="cp-slug-input"
            />
          </div>
          <div className="cp-slug-row-actions">
            <div className={`cp-slug-hint ${slugState.available === true ? "ok" : slugState.available === false ? "bad" : ""}`} data-testid="cp-slug-hint">{slugHint}</div>
            <div style={{ display: "flex", gap: 6 }}>
              {campaign.slug && slug.trim() !== campaign.slug && (
                <button className="ads-btn ads-btn-ghost" style={{ padding: "4px 10px", fontSize: 11 }} onClick={clearSlug} data-testid="cp-slug-clear">
                  Remove
                </button>
              )}
              <button
                className="ads-btn ads-btn-primary"
                style={{ padding: "4px 12px", fontSize: 11 }}
                onClick={saveSlug}
                disabled={!canSaveSlug}
                data-testid="cp-slug-save"
              >
                {slugState.saving ? <Loader2 size={11} className="animate-spin" /> : "Save URL"}
              </button>
            </div>
          </div>
        </div>

        <div className="cp-link-row">
          <input className="ads-prop-input" value={url} readOnly data-testid="cp-share-url" onFocus={(e) => e.target.select()} />
          <button className="ads-btn ads-btn-primary" onClick={copy} data-testid="cp-share-copy">
            {copied ? <><Check size={14} /> Copied</> : <><Link2 size={14} /> Copy</>}
          </button>
        </div>

        {/* Lead capture toggle */}
        <button
          type="button"
          className={`cp-leads-toggle ${leadsOn ? "is-on" : ""}`}
          onClick={toggleLeads}
          disabled={leadsSaving}
          data-testid="cp-leads-toggle"
        >
          <div className="cp-leads-toggle-copy">
            <div className="cp-leads-toggle-title">
              {leadsOn ? <ToggleRight size={16} /> : <ToggleLeft size={16} />}
              Lead capture widget: <b>{leadsOn ? "ON" : "OFF"}</b>
            </div>
            <div className="cp-leads-toggle-sub">Collect emails from visitors + send a "claim 3 free credits" link.</div>
          </div>
          {leadsSaving && <Loader2 size={14} className="animate-spin" />}
        </button>

        {typeof navigator !== "undefined" && navigator.share && (
          <button
            className="ads-btn ads-btn-ghost"
            style={{ width: "100%", justifyContent: "center", marginBottom: 14 }}
            onClick={nativeShare}
            data-testid="cp-share-native"
          >
            <Share2 size={14} /> Share via device…
          </button>
        )}

        <div className="cp-social-grid">
          {channels.map((c) => (
            <a
              key={c.id}
              href={c.href}
              target={c.id === "email" ? "_self" : "_blank"}
              rel="noreferrer"
              className="cp-social-btn"
              data-testid={`cp-share-${c.id}`}
              style={{ "--ch": c.color }}
              onClick={() => adsApiCall.trackCampaign(campaign.campaign_id, "share", c.id).catch(() => {})}
            >
              <c.icon size={16} />
              <span>{c.label}</span>
            </a>
          ))}
        </div>

        <Link
          to={`/c/${campaign.slug || campaign.campaign_id}`}
          target="_blank"
          rel="noreferrer"
          className="ads-btn ads-btn-ghost"
          style={{ width: "100%", justifyContent: "center", marginTop: 12 }}
        >
          Preview public page
        </Link>
      </div>
    </div>
  );
}
